import { Fragment, useEffect } from 'react';

import TotalItemPagination from '@zola/zola-ui/src/components/Pagination/TotalItemPagination';

import cx from 'classnames';
import { forceCheck } from 'react-lazyload';

import Loader from '~/components/common/ui/loader/Loader';
import { useSearchResultsPageContext } from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageContext';
import { useVendorSearchNavigation } from '~/pages/couples/explore/components/SearchResults/contexts/VendorSearchNavigationContext';
import { isSearchLocation } from '~/util/searchUtils';

import { PageSection } from '../../../PageSection/PageSection';
import { isQuizCardView } from '../../../VendorCardGrid/utils';
import { VendorCardGrid } from '../../../VendorCardGrid/VendorCardGrid';
import { useGridLayout } from '../../hooks/useGridLayout';
import { SearchResultCardView, VendorSearchRequest } from '../../types/types';
import { LowSupplyShelf } from '../LowSupply/LowSupplyShelf';
import { NoResults } from '../NoResults/NoResults';

import styles from './vendorSearchResults.module.less';

export type VendorSearchResultsProps = {
  searchResults: SearchResultCardView[];
  isLoading: boolean;
  handleClearFilters: () => void;
};

export type VendorSearchResultsContentProps = VendorSearchResultsProps & {
  canonicalUrl: string;
  vendorSearch: VendorSearchRequest;
  offset: number;
  totalHits: number;
  supplied: boolean;
};

export const VendorSearchResultsContent = ({
  canonicalUrl,
  searchResults,
  isLoading,
  handleClearFilters,
  vendorSearch,
  offset,
  totalHits,
  supplied,
}: VendorSearchResultsContentProps) => {
  const { selectedLocation, selectedTaxonomy, page } = vendorSearch;

  const { navigateToSrp } = useVendorSearchNavigation();

  const { numRows, perRow } = useGridLayout();

  // When the vendors list changes, the visible images change without any scroll event happening.
  // Double check here if lazy load images need to be loaded
  useEffect(() => {
    forceCheck();
  }, [searchResults]);

  const showLoadingState = isLoading && searchResults.length === 0;

  if (showLoadingState) {
    return (
      <div className={cx({ [styles.loading]: showLoadingState })}>
        <div style={{ minHeight: '700px', margin: '0 auto' }}>
          <Loader delay={false} />
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      {searchResults.filter((card) => !isQuizCardView(card)).length > 0 && (
        <PageSection stretchToEdge contentClassName={styles.searchContainer}>
          <VendorCardGrid
            searchResults={searchResults}
            vendorSearch={vendorSearch}
            offset={offset}
            isLoading={isLoading}
            listingShelfName={'SEARCH_RESULTS_PAGE'}
          />
        </PageSection>
      )}

      <PageSection contentClassName={cx(styles.paginationControls)} noPadding>
        {totalHits > 0 && supplied && (
          <TotalItemPagination
            className={styles.paginationContainer}
            currentPage={page}
            currentUrl={canonicalUrl}
            maxPageCountToShow={104}
            onClick={(event, newPage) => {
              // Override the href behavior to scroll to top and navigate to new page client-side
              event.preventDefault();
              window.scrollTo(0, 0);
              navigateToSrp({ page: newPage });
            }}
            pageSize={perRow * numRows}
            totalItems={totalHits}
          />
        )}
      </PageSection>
      {!isLoading && searchResults.filter((card) => !isQuizCardView(card)).length < 1 && (
        <PageSection>
          <NoResults handleClearFilters={handleClearFilters} />
        </PageSection>
      )}

      {supplied === false && isSearchLocation(selectedLocation) && (
        <LowSupplyShelf taxonomyKey={selectedTaxonomy?.key} location={selectedLocation} />
      )}
    </Fragment>
  );
};

/**
 * Handles the displaying of vendor result searchResults in rows, or if there are no searchResults,
 * or if ts low supply market, showing the appropriate content.
 */
export const VendorSearchResults = (props: VendorSearchResultsProps): JSX.Element => {
  const {
    vendorSearch,
    supplied,
    searchResults: { offset, totalHits, shouldInsertBudgetQuiz, shouldInsertMiniQuiz },
    canonicalUrl,
  } = useSearchResultsPageContext();

  const numSlotsUsedFirstPage = [shouldInsertMiniQuiz, shouldInsertBudgetQuiz].filter(
    Boolean
  ).length;

  return (
    <VendorSearchResultsContent
      {...props}
      canonicalUrl={canonicalUrl}
      totalHits={totalHits + numSlotsUsedFirstPage}
      supplied={Boolean(supplied)}
      offset={offset}
      vendorSearch={vendorSearch}
    />
  );
};
